// src/components/ui/modal/StatusCardModal.tsx
'use client';

import type { ReactNode } from 'react';
import clsx from 'clsx';
import Link from 'next/link';
import Image from 'next/image';
import CardModal from './CardModal';
import style from './StatusCardModal.module.scss';
import CoinInWalletAnim from './components/CoinInWalletAnim';
import { IconCheckDouble } from '@/components/icons';

type Cover = { bgVariant?: '1' | '2' | '3'; bgImage?: string };
type StatusConfigItem = {
  cover: Cover;
  title: string;
  subtitle?: string;
  desc?: string;
  /** media：成功用動畫，其它用 iconSrc */
  useAnim: boolean;
  iconSrc?: string;
  actions: ActionItem[];
};
type StatusConfigMap = Record<StatusKind, StatusConfigItem>;

export type StatusKind = 'success' | 'alreadyGot' | 'outOfStock' | 'error' | 'taskDone';

export type ActionItem = {
  key: string; // 唯一鍵
  text: string; // 顯示文字
  href?: string; // 以 / 開頭 → 用 <Link>；其他→ <a target="_blank">
  onClick?: () => void | Promise<void>; // 無 href 時使用
  className?: string; // 例：'btn-red'、'btn-outline-secondary'
  disabled?: boolean;
  closeAfter?: 'always' | 'never'; // 預設 'always'
};

export type StatusCardModalProps = {
  open: boolean;
  onClose: () => void;
  status: StatusKind;

  /** 可覆蓋內建文案 */
  title?: string;
  subtitle?: string;
  desc?: string;

  /** success 狀態動畫參數（其他狀態顯示靜態圖） */
  color?: 'green' | 'red' | 'yellow';
  avatarSrc?: string;
  scale?: number;
  delayMs?: number;
  soundUrl?: string;
  playSound?: boolean;
  soundVolume?: number;

  /** 單次覆蓋封面（本次渲染優先） */
  bgVariant?: '1' | '2' | '3';
  bgImage?: string;

  /** 依「狀態」覆蓋封面（集中客製） */
  coverOverrides?: Partial<Record<StatusKind, { bgVariant?: '1' | '2' | '3'; bgImage?: string }>>;

  /** 外觀 */
  dialogSize?: 'sm' | 'lg' | 'xl';
  className?: string;
  closeOnBackdrop?: boolean;
  lockScroll?: boolean;
  showCloseButton?: boolean;

  /** 常用 callback（供預設動作在沒有 href 時使用） */
  onViewWallet?: () => void | Promise<void>;
  onRetry?: () => void | Promise<void>;
  onGoTasks?: () => void | Promise<void>;
  onCloseText?: string;

  /** 統一命名：成功 / 已領取過 狀態預設第一顆按鈕連結 */
  viewHref?: string;

  /** 直接覆蓋該狀態的整組 actions */
  actionsOverride?: Partial<Record<StatusKind, ActionItem[]>>;
  /** 在該狀態預設 actions 後面追加按鈕 */
  actionsAppend?: Partial<Record<StatusKind, ActionItem[]>>;
};

const DEFAULT_SOUND_URL = 'https://d1q14jmvwk39e0.cloudfront.net/public/assets/mp3/capsule.mp3';

/* ---------- builders ---------- */

function buildActions(items?: ActionItem[], onClose?: () => void): ReactNode | null {
  if (!items || items.length === 0) return null;

  const handle = (item: ActionItem) => async (e?: React.MouseEvent) => {
    if (item.disabled) {
      e?.preventDefault?.();
      return;
    }
    try {
      if (item.onClick) await item.onClick();
    } finally {
      if (item.closeAfter !== 'never') onClose?.();
    }
  };

  return (
    <div className="d-grid gap-2">
      {items.map((it) => {
        const className = clsx('btn w-100', it.className ?? 'btn-outline-secondary');

        // 內部連結：Next.js Link
        if (it.href && it.href.startsWith('/')) {
          return (
            <Link
              key={it.key}
              href={it.href}
              className={className}
              aria-disabled={it.disabled}
              onClick={(e) => {
                if (it.disabled) {
                  e.preventDefault();
                  return;
                }
                if (it.closeAfter !== 'never') onClose?.();
              }}
            >
              {it.text}
            </Link>
          );
        }

        // 外部連結
        if (it.href) {
          return (
            <a
              key={it.key}
              href={it.href}
              className={className}
              aria-disabled={it.disabled}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                if (it.disabled) {
                  e.preventDefault();
                  return;
                }
                if (it.closeAfter !== 'never') onClose?.();
              }}
            >
              {it.text}
            </a>
          );
        }

        // 無 href：button
        return (
          <button
            key={it.key}
            type="button"
            className={className}
            onClick={handle(it)}
            disabled={it.disabled}
          >
            {it.text}
          </button>
        );
      })}
    </div>
  );
}

function buildContent(title?: string, subtitle?: string, desc?: string): ReactNode | null {
  if (!title && !subtitle && !desc) return null;
  return (
    <>
      {subtitle && (
        <div className="text-success mt-2 fs-5">
          <IconCheckDouble width={24} />
          {subtitle}
        </div>
      )}
      {title && <h5 className="media-heading mt-4 mb-2">{title}</h5>}
      {desc && <div className={clsx('mb-2', style.desc)}>{desc}</div>}
    </>
  );
}

/** 每個狀態集中設定（封面、圖示/動畫、文案、按鈕） */
function buildStatusConfig(p: StatusCardModalProps): Record<
  StatusKind,
  {
    cover: { bgVariant?: '1' | '2' | '3'; bgImage?: string };
    title: string;
    subtitle?: string;
    desc?: string;
    /** media：成功用動畫，其它用 iconSrc */
    useAnim: boolean;
    iconSrc?: string;
    actions: ActionItem[];
  }
> {
  const closeText = p.onCloseText ?? '知道了';

  const cfg: StatusConfigMap = {
    success: {
      cover: { bgVariant: '1' },
      title: '恭喜您獲得美食籌碼！',
      //subtitle: '已成功發佈',
      desc: '已將籌碼放入你的籌碼包囉~',
      useAnim: true,
      actions: [
        {
          key: 'view',
          text: '查看籌碼包',
          href: p.viewHref ?? '/me/wallet',
          className: 'btn-red',
          closeAfter: 'always',
        },
        {
          key: 'explore',
          text: '再逛逛',
          //href: '/',
          className: 'btn-outline-secondary',
          closeAfter: 'always',
        },
      ],
    },
    alreadyGot: {
      cover: { bgVariant: '2' },
      title: '您已領取過此籌碼囉~',
      subtitle: undefined,
      desc: '',
      useAnim: false,
      iconSrc:
        'https://d1q14jmvwk39e0.cloudfront.net/public/assets/images/icon/icon_kol_link_got.svg',
      actions: [
        {
          key: 'view',
          text: '查看籌碼包',
          href: p.viewHref ?? 'me/wallet',
          className: 'btn-red',
          closeAfter: 'always',
        },
      ],
    },
    outOfStock: {
      cover: { bgVariant: '2' },
      title: '商家籌碼已發完!',
      subtitle: undefined,
      desc: '好可惜!籌碼已發完，您可以繼續寫籌碼心情唷 ～',
      useAnim: false,
      iconSrc:
        'https://d1q14jmvwk39e0.cloudfront.net/public/assets/images/icon/icon_kol_link_no.svg',
      actions: [
        {
          key: 'events',
          text: '看看其他活動',
          href: '/',
          className: 'btn-red',
          closeAfter: 'always',
        },
      ],
    },
    error: {
      cover: { bgVariant: '2' },
      title: 'Oops！發生錯誤',
      subtitle: undefined,
      desc: '請稍後再試，或聯絡客服。',
      useAnim: false,
      iconSrc:
        'https://d1q14jmvwk39e0.cloudfront.net/public/assets/images/icon/icon_kol_link_warning_sign.png',
      actions: [
        {
          key: 'retry',
          text: '重新嘗試',
          onClick: p.onRetry,
          className: 'btn-red',
          closeAfter: 'never',
        },
        { key: 'ok', text: closeText, className: 'btn-outline-secondary', closeAfter: 'always' },
      ],
    },
    taskDone: {
      cover: { bgVariant: '1' },
      title: '任務完成！',
      subtitle: undefined,
      desc: '恭喜完成任務，已發送獎勵。',
      useAnim: false,
      iconSrc:
        'https://d1q14jmvwk39e0.cloudfront.net/public/assets/images/icon/icon_task_accomplished.svg',
      actions: [
        {
          key: 'tasks',
          text: '前往任務牆',
          href: '/',
          className: 'btn-red',
          closeAfter: 'always',
        },
      ],
    },
  };

  // 保險：第一顆沒 href/沒 onClick → 補 callback
  (Object.keys(cfg) as StatusKind[]).forEach((k) => {
    const first = cfg[k].actions[0];
    if (first && !first.href && !first.onClick) {
      if (k === 'success' || k === 'alreadyGot') first.onClick = p.onViewWallet;
      if (k === 'error') first.onClick = p.onRetry;
      if (k === 'taskDone') first.onClick = p.onGoTasks;
    }
  });

  // 依狀態覆蓋封面（集中客製）
  if (p.coverOverrides) {
    (Object.keys(p.coverOverrides) as StatusKind[]).forEach((k) => {
      const override = p.coverOverrides?.[k];
      if (override) {
        cfg[k].cover = { ...cfg[k].cover, ...override };
      }
    });
  }

  return cfg as ReturnType<typeof buildStatusConfig>;
}

function buildMediaNode(
  status: StatusKind,
  p: StatusCardModalProps,
  useAnim: boolean,
  iconSrc?: string
): ReactNode {
  if (useAnim) {
    const {
      color = 'green',
      avatarSrc = 'https://d1q14jmvwk39e0.cloudfront.net/public/assets/images/user/avatar01.jpg',
      scale = 1.2,
      delayMs = 0,
      soundUrl = DEFAULT_SOUND_URL,
      playSound = true,
      soundVolume = 1,
    } = p;

    return (
      <CoinInWalletAnim
        color={color}
        avatarSrc={avatarSrc}
        scale={scale}
        delayMs={delayMs}
        soundUrl={soundUrl}
        playSound={playSound}
        soundVolume={soundVolume}
      />
    );
  }

  if (iconSrc) {
    return (
      <Image src={iconSrc} alt={status} width={160} height={160} className={style.statusPic} />
    );
  }
  return null;
}

/* ---------- component ---------- */

const StatusCardModal: React.FC<StatusCardModalProps> = (props) => {
  const {
    open,
    onClose,
    status,

    // 可覆蓋文案
    title,
    subtitle,
    desc,

    // 單次覆蓋封面
    bgVariant,
    bgImage,

    // 外觀
    dialogSize = 'sm',
    className,
    closeOnBackdrop = true,
    lockScroll = true,
    showCloseButton = true,

    // actions 擴充
    actionsOverride,
    actionsAppend,
  } = props;

  // 集中式狀態設定（含封面、文案、媒體、按鈕）
  const CONFIG = buildStatusConfig(props);
  const base = CONFIG[status];

  // 文案支援覆蓋
  const finalTitle = title ?? base.title;
  const finalSubtitle = subtitle ?? base.subtitle;
  const finalDesc = desc ?? base.desc;

  // 封面：狀態預設 → 單次覆蓋（本次渲染優先）
  const finalCover = {
    ...base.cover,
    ...(bgVariant || bgImage ? { bgVariant, bgImage } : null),
  };

  // 媒體
  const mediaNode = buildMediaNode(status, props, base.useAnim, base.iconSrc);

  // actions：預設 → 覆蓋 → 附加 → 保底
  let finalActions: ActionItem[] = base.actions;
  if (actionsOverride?.[status]) finalActions = actionsOverride[status]!;
  if (actionsAppend?.[status]?.length) finalActions = [...finalActions, ...actionsAppend[status]!];
  if (!finalActions.length)
    finalActions = [
      {
        key: 'ok',
        text: props.onCloseText ?? '關閉',
        onClick: onClose,
        className: 'btn-outline-secondary',
        closeAfter: 'always',
      },
    ];

  // Slots
  const content = buildContent(finalTitle, finalSubtitle, finalDesc);
  const actions = buildActions(finalActions, onClose);

  return (
    <CardModal
      open={open}
      onClose={onClose}
      ariaTitle={finalTitle}
      dialogSize={dialogSize}
      className={className}
      closeOnBackdrop={closeOnBackdrop}
      lockScroll={lockScroll}
      showCloseButton={showCloseButton}
      /** 直接以狀態設定好的封面為主，再由本次渲染覆蓋 */
      bgVariant={finalCover.bgVariant}
      bgImage={finalCover.bgImage}
      /** 其他插槽 */
      media={mediaNode}
      content={content}
      actions={actions}
      classNames={{
        cover: style.cardTop,
        media: style.cardImage,
      }}
    />
  );
};

export default StatusCardModal;
